import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MedicalLessonDetail, MedicalModuleView, MedicalModuleGrid } from '../components/medical/MedicalComponents';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Stethoscope, Activity, Pill, AlertTriangle, Loader2, Play, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MedicalGerman() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vocabulary, setVocabulary] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('vitals');
  const [practiceScenario, setPracticeScenario] = useState(null);
  const [generatingScenario, setGeneratingScenario] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);

  useEffect(() => {
    fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/medical-german/modules`);
      setModules(response.data.modules || []);
    } catch (error) {
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleClick = (module) => {
    setSelectedModule(module);
    setSelectedLesson(null);
  };

  const handleLessonClick = async (lesson) => {
    setLessonLoading(true);
    try {
      const response = await axios.get(`${API}/medical-german/lessons/${lesson.lesson_id}`);
      setSelectedLesson(response.data);
    } catch (error) {
      toast.error('Failed to load lesson');
      setSelectedLesson(lesson);
    } finally {
      setLessonLoading(false);
    }
  };

  const fetchVocabulary = async (category) => {
    setSelectedCategory(category);
    try {
      const response = await axios.get(`${API}/flashcards/dictionary?category=medical_${category}`);
      setVocabulary(response.data.cards || []);
    } catch (error) {
      toast.error('Failed to load vocabulary');
    }
  };

  const generatePracticeScenario = async (scenarioType) => {
    setGeneratingScenario(true);
    try {
      const response = await axios.post(`${API}/medical-german/practice`, { scenario_type: scenarioType });
      setPracticeScenario(response.data);
    } catch (error) {
      toast.error('Failed to generate scenario');
    } finally {
      setGeneratingScenario(false);
    }
  };

  const getTitle = (item) => {
    if (language === 'ar') return item.title_ar || item.title_de;
    if (language === 'en') return item.title_en || item.title_de;
    return item.title_de;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="medical-german-page">
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-white" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white">Medizinisches Deutsch</h1>
            <p className="text-sm text-slate-400">Medical German · الألمانية الطبية</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="modules">Modules</TabsTrigger>
            <TabsTrigger value="vocabulary">Vocabulary</TabsTrigger>
            <TabsTrigger value="practice">Practice</TabsTrigger>
          </TabsList>

          <TabsContent value="modules">
            {selectedLesson ? (
              <MedicalLessonDetail lesson={selectedLesson} onBack={() => setSelectedLesson(null)} />
            ) : selectedModule ? (
              <MedicalModuleView module={selectedModule} onBack={() => setSelectedModule(null)} onLessonClick={handleLessonClick} lessonLoading={lessonLoading} />
            ) : (
              <MedicalModuleGrid modules={modules} getTitle={getTitle} onModuleClick={handleModuleClick} />
            )}
          </TabsContent>

          <TabsContent value="vocabulary">
            <VocabularyTab vocabulary={vocabulary} selectedCategory={selectedCategory} onFetchVocabulary={fetchVocabulary} />
          </TabsContent>

          <TabsContent value="practice">
            <PracticeTab practiceScenario={practiceScenario} generatingScenario={generatingScenario} onGenerate={generatePracticeScenario} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function VocabularyTab({ vocabulary, selectedCategory, onFetchVocabulary }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {['vitals', 'pain', 'medication', 'emergency'].map((cat) => (
          <Button key={cat} variant={selectedCategory === cat ? 'default' : 'outline'} onClick={() => onFetchVocabulary(cat)}
            className={selectedCategory === cat ? 'bg-emerald-500 hover:bg-emerald-600' : 'text-white border-slate-600'}>
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {vocabulary.map((word, index) => (
          <Card key={word.card_id || word.front_de || `word-${index}`} className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{word.front_de || word.de}</h3>
                    <AudioButton text={word.audio_text || word.front_de || word.de} voice="nova" size="sm" className="text-emerald-400" />
                    <MicrophoneButton expectedText={word.front_de || word.de} onPronunciationScore={(s) => { if (s.overall_score >= 80) toast.success(`Excellent! ${s.overall_score}%`); }} size="sm" className="text-emerald-400" />
                  </div>
                  <p className="text-emerald-400">{word.front_en || word.en}</p>
                  <p className="text-slate-400 text-sm" dir="rtl">{word.front_ar || word.ar}</p>
                  {(word.back_example || word.example) && (
                    <div className="flex items-center gap-1 mt-2">
                      <p className="text-slate-500 text-sm italic">&quot;{word.back_example || word.example}&quot;</p>
                      <AudioButton text={word.back_example || word.example} voice="nova" size="sm" className="text-slate-500 hover:text-emerald-300" />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {vocabulary.length === 0 && <p className="text-slate-400 col-span-2 text-center py-8">Select a category to view vocabulary</p>}
      </div>
    </div>
  );
}

function PracticeTab({ practiceScenario, generatingScenario, onGenerate }) {
  const SCENARIOS = [
    { type: 'admission', label: 'Patient Admission', icon: Stethoscope },
    { type: 'vitals', label: 'Vital Signs', icon: Activity },
    { type: 'medication', label: 'Medication', icon: Pill },
    { type: 'pain', label: 'Pain Assessment', icon: AlertTriangle },
    { type: 'emergency', label: 'Emergency', icon: AlertTriangle },
    { type: 'discharge', label: 'Discharge', icon: Check },
  ];
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        {SCENARIOS.map((scenario) => (
          <Button key={scenario.type} variant="outline" className="h-24 flex flex-col items-center justify-center gap-2 bg-slate-800/50 border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 text-white"
            onClick={() => onGenerate(scenario.type)} disabled={generatingScenario}>
            <scenario.icon className="h-6 w-6 text-emerald-400" />
            <span>{scenario.label}</span>
            {generatingScenario && <Loader2 className="h-4 w-4 animate-spin" />}
          </Button>
        ))}
      </div>
      {practiceScenario && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Play className="h-5 w-5 text-emerald-400" />
              {practiceScenario.scenario_type.charAt(0).toUpperCase() + practiceScenario.scenario_type.slice(1)} Scenario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <pre className="whitespace-pre-wrap text-slate-300 bg-slate-900/50 p-4 rounded-lg text-sm">{practiceScenario.content}</pre>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
              <AudioButton text={practiceScenario.content} voice="nova" size="default" className="text-emerald-400" />
              <span className="text-slate-400 text-sm">Listen to scenario</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
